import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getLeadCallQueue } from "@/lib/apiClient";
import { Phone, AlertTriangle, CalendarClock, Sparkles, Ghost, CalendarDays, UserCheck, StickyNote } from "lucide-react";

export const GROUPS = [
  { id: "overdue", label: "Te laat", hint: "Terugbelafspraak is verlopen", icon: AlertTriangle, color: "#B0413E", bg: "#F3DEDD" },
  { id: "today", label: "Vandaag", hint: "Gepland om vandaag te bellen", icon: CalendarClock, color: "#0C6E6E", bg: "#D6F3F3" },
  { id: "new", label: "Nieuw, nog nooit gebeld", hint: "Zo snel mogelijk eerste contact", icon: Sparkles, color: "#001C51", bg: "#E3E9F5" },
  { id: "forgotten", label: "Vergeten (7+ dagen stil)", hint: "Geen opvolgdatum en al een week geen contact", icon: Ghost, color: "#B7791F", bg: "#FFF4DB" },
  { id: "tomorrow", label: "Morgen", hint: "Alvast voorbereiden", icon: CalendarDays, color: "#5B6675", bg: "#EDF1F5" },
];

export const contactLine = (l) => (l.last_contact_name ? `Gecontacteerd door ${l.last_contact_name} · ${new Date(l.last_contact_at).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}` : "Nog niet gecontacteerd");

let cache = { at: 0, data: null };
export function useCallQueue(pollMs = 60000) {
  const [data, setData] = useState(cache.data);
  useEffect(() => {
    let alive = true;
    const tick = () => getLeadCallQueue().then((d) => { cache = { at: Date.now(), data: d }; if (alive) setData(d); }).catch(() => {});
    if (Date.now() - cache.at > 5000) tick();
    const t = setInterval(tick, pollMs);
    return () => { alive = false; clearInterval(t); };
  }, [pollMs]);
  return data;
}

export function CallQueueBadge({ className = "" }) {
  const q = useCallQueue();
  if (!q?.to_call) return null;
  return <span data-testid="call-queue-badge" title="Te laat + vandaag te bellen" className={`min-w-[20px] h-5 px-1.5 rounded-full bg-[#B0413E] text-white text-[11px] font-bold flex items-center justify-center ${className}`}>{q.to_call}</span>;
}

function Row({ l, navigate }) {
  return (
    <div data-testid={`queue-lead-${l.id}`} className="px-5 py-3 border-t border-[#EDF1F5] flex flex-wrap items-center gap-3 hover:bg-[#F6F9FB]">
      <div className="min-w-[160px]">
        <p className="font-medium text-[#16202B] flex items-center gap-1.5">{l.name}{l.has_admin_note && <StickyNote className="h-3.5 w-3.5 text-[#B7791F]" title="Notitie van admin" />}</p>
        <p className="text-xs text-[#5B6675]">{l.phone || "—"}{l.follow_up_date ? ` · terugbellen ${new Date(l.follow_up_date).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}` : ""}</p>
      </div>
      <span className={`text-xs px-2 py-0.5 rounded-full ${l.assigned_user_name ? "bg-[#E3E9F5] text-[#001C51]" : "bg-[#EDF1F5] text-[#8994A3]"}`}><UserCheck className="inline h-3 w-3 mr-1" />{l.assigned_user_name || "Niet toegewezen"}</span>
      <span className="text-xs text-[#5B6675]">{contactLine(l)}</span>
      {l.phone && <button data-testid={`queue-call-${l.id}`} onClick={() => navigate(`/belomgeving/${l.id}`)} className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#001C51] text-white text-sm font-medium hover:bg-[#0A337A]"><Phone className="h-4 w-4" /> Bel</button>}
    </div>
  );
}

export function CallQueue() {
  const q = useCallQueue(30000);
  const navigate = useNavigate();
  const [open, setOpen] = useState("overdue");
  useEffect(() => { if (q && !q.counts[open]) setOpen(GROUPS.find((g) => q.counts[g.id])?.id || "overdue"); }, [q?.counts]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!q) return null;
  const items = q.groups[open] || [];
  return (
    <section className="rounded-2xl border border-[#E2E7EC] bg-white overflow-hidden mb-8" data-testid="call-queue">
      <div className="px-6 py-4 flex flex-wrap items-center gap-3">
        <h2 className="font-heading font-semibold text-xl text-[#16202B]">Bellijst van vandaag</h2>
        {q.next && <button data-testid="queue-start" onClick={() => navigate(`/belomgeving/${q.next.id}`)} className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#16BFBF] text-white font-semibold hover:bg-[#12A8A8]"><Phone className="h-4 w-4" /> Start met bellen: {q.next.name}</button>}
      </div>
      <div className="px-6 pb-4 grid grid-cols-2 md:grid-cols-5 gap-2">
        {GROUPS.map((g) => (
          <button key={g.id} data-testid={`queue-group-${g.id}`} onClick={() => setOpen(g.id)}
            className={`text-left rounded-xl border p-3 transition-colors ${open === g.id ? "border-[#001C51] ring-1 ring-[#001C51]" : "border-[#E2E7EC] hover:bg-[#F6F9FB]"}`}>
            <span className="flex items-center gap-2 text-xs font-semibold" style={{ color: g.color }}><span className="h-6 w-6 rounded-lg flex items-center justify-center" style={{ background: g.bg }}><g.icon className="h-3.5 w-3.5" /></span>{g.label}</span>
            <span className="block mt-1 font-heading text-2xl font-bold text-[#16202B]" data-testid={`queue-count-${g.id}`}>{q.counts[g.id]}</span>
          </button>
        ))}
      </div>
      <p className="px-6 pb-2 text-xs text-[#5B6675]">{GROUPS.find((g) => g.id === open).hint}</p>
      <div className="max-h-[420px] overflow-y-auto fe-scroll">
        {items.length === 0 ? <p className="px-6 py-6 text-sm text-[#5B6675] border-t border-[#EDF1F5]">Niets in deze groep. Goed bezig!</p>
          : items.slice(0, 100).map((l) => <Row key={l.id} l={l} navigate={navigate} />)}
      </div>
    </section>
  );
}
